import React from "react";
// mui
import {
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  IconButton,
  Typography,
  Box,
  Tooltip,
} from "@mui/material";
// enums
import { formatLabel } from "utils/text";
// utils
import {
  SubscriptionProps,
  StatCardProps,
  StatConfig,
  SubscriptionStatus,
} from "./types/main";
// utils
import { chunkArray } from "utils/array";
// assets
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as TierIcon } from "./assets/imgs/tier.svg";
import { ReactComponent as AccountTypeIcon } from "./assets/imgs/account-type.svg";
import { ReactComponent as ClientCountIcon } from "./assets/imgs/client-count.svg";
import { ReactComponent as MemberCountIcon } from "./assets/imgs/member-count.svg";
import { ReactComponent as InvoiceCountIcon } from "./assets/imgs/invoice-count.svg";
import { ReactComponent as ProjectCountIcon } from "./assets/imgs/project-count.svg";
import { ReactComponent as MilestoneCountIcon } from "./assets/imgs/milestone-count.svg";
import { ReactComponent as ReportCountIcon } from "./assets/imgs/report-count.svg";
import { ReactComponent as TaskCountIcon } from "./assets/imgs/task-count.svg";
import { ReactComponent as ScreenshotLifetimeIcon } from "./assets/imgs/screenshot-lifetime.svg";
import { ReactComponent as PriceIcon } from "./assets/imgs/price.svg";
import { ReactComponent as DiscountAmountIcon } from "./assets/imgs/discount-amount.svg";
import { ReactComponent as RankIcon } from "./assets/imgs/rank.svg";
import { ReactComponent as StatusIcon } from "./assets/imgs/status.svg";
import { ReactComponent as HistoryDaysIcon } from "./assets/imgs/history-days.svg";
import editingStyles from "assets/scss/editing.module.scss";
import styles from "./assets/scss/subscriptions.module.scss";

const StatCard: React.FC<StatCardProps> = ({ icon, label, value }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        minWidth: 180,
        maxWidth: 400,
      }}
    >
      <div className={styles["align-center"]}>{icon}</div>
      <div
        className={styles["align-center"]}
        style={{ flexDirection: "column" }}
      >
        <Tooltip title={label}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              fontSize: "0.95rem",
              color: "#000",
              mb: 0,
              maxWidth: 175,
              whiteSpace: "wrap",
              overflow: "hidden",
              textAlign: "center",
            }}
          >
            {label}
          </Typography>
        </Tooltip>
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: "0.9rem",
            color: theme.custom.font.lighter,
          }}
        >
          {value}
        </Typography>
      </div>
    </Box>
  );
};

const statsConfig: StatConfig[] = [
  { key: "tier", icon: <TierIcon /> },
  {
    key: "account_type",
    icon: <AccountTypeIcon />,
  },
  {
    key: "status",
    icon: <StatusIcon />,
  },
  {
    key: "member_count",
    icon: <MemberCountIcon />,
  },
  {
    key: "client_count",
    icon: <ClientCountIcon />,
  },
  {
    key: "project_count",
    icon: <ProjectCountIcon />,
  },
  {
    key: "milestone_count",
    icon: <MilestoneCountIcon />,
  },
  {
    key: "task_count",
    icon: <TaskCountIcon />,
  },
  {
    key: "invoice_count",
    icon: <InvoiceCountIcon />,
  },
  {
    key: "report_count",
    icon: <ReportCountIcon />,
  },
  {
    key: "rank",
    icon: <RankIcon />,
  },
  {
    key: "price",
    icon: <PriceIcon />,
  },
  {
    key: "discount_amount",
    icon: <DiscountAmountIcon />,
  },
  {
    key: "screenshots_lifetime",
    icon: <ScreenshotLifetimeIcon />,
  },
  {
    key: "task_history_days",
    icon: <HistoryDaysIcon />,
  },
  {
    key: "time_tracking_report_history_days",
    icon: <HistoryDaysIcon />,
  },
  {
    key: "task_details_report_history_days",
    icon: <HistoryDaysIcon />,
  },
];
const ViewSubscription: React.FC<SubscriptionProps> = ({
  onClose,
  subscription,
}) => {
  const theme = useTheme();

  return (
    <Dialog
      open
      onClose={onClose}
      PaperProps={{
        component: "form",
      }}
      className={editingStyles["popup-base"]}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          color: theme.palette.primary.main,
        }}
      >
        Subscription Details
        <IconButton
          onClick={onClose}
          sx={{
            width: "auto",
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pb: 4 }}>
        {chunkArray(statsConfig, 2).map((row, rowIndex) => (
          <div key={rowIndex} className={styles["view-details-container"]}>
            {row.map(({ key, icon }) => (
              <StatCard
                key={key}
                icon={icon}
                label={formatLabel(key)}
                value={
                  key === "status"
                    ? SubscriptionStatus[subscription.status]
                    : subscription[key]
                }
              />
            ))}
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};
export default ViewSubscription;
